import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Save, RefreshCw, Upload, X } from 'lucide-react';
import { Base_Url, label, lableView } from '../../../../config/config';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../component/b2c/navBar/navBar';
import { useMyContext } from '../../../../context/MyContext';
import '../../../../css/custom-scrollbar.css';
import { toast } from 'react-toastify';
import axios from 'axios';

const ShippingLabelCustomizer = () => {

    const { isDarkMode, isToggleSidebar } = useMyContext();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [brandLogoFile, setBrandLogoFile] = useState(null);
    const [brandLogoPreview, setBrandLogoPreview] = useState(null);
    const [existingLogo, setExistingLogo] = useState(null);
    const [placeholderLogo] = useState('/assets/images/logo1.png');

    const [visibilitySettings, setVisibilitySettings] = useState({
        brand_logo: true,
        shipping_address: true,
        shipping_pincode: true,
        payment_mode: true,
        shipment_type: true,
        invoice_amount: true,
        seller_name: true,
        seller_address: true,
        seller_pin: true,
        weight: true,
        product_name: true,
        price: true,
        return_address: true,
        default_logo: true
    });


    const fieldLabels = {
        brand_logo: 'Brand Logo (Uploaded)',
        shipping_address: 'Shipping Address',
        shipping_pincode: 'Shipping Pincode',
        payment_mode: 'Payment Mode',
        shipment_type: 'Shipment Type',
        invoice_amount: 'Invoice Amount',
        seller_name: 'Seller Name',
        seller_address: 'Seller Address',
        seller_pin: 'Seller Pin',
        weight: 'Weight',
        product_name: 'Product Name',
        price: 'Price',
        return_address: 'Return Address',
        default_logo: 'Default Logo'
    };

    const staticLabelData = {
        order_id: "***************",
        first_name: "First Name",
        last_name: "Last name",
        drop_address: "Drop Address ",
        drop_city: "Drop City",
        drop_state: "Drop State",
        drop_pin: "*******",
        cluster_code: "***",
        destination_code: "***",
        destination_location: "*****",
        payment_mode: "Payment Mode",
        shipping_mode: "Shipping Mode",
        cod_amount: "***.**",
        seller_name: "Seller name",
        seller_address: "Seller Address With Pincode",
        seller_pin: "******",
        weight: "***.***",
        partner: "Courier Parner name",
        product_desc: "Product Description",
        total_amount: "*****.**",
        waybill: "90********00",
        return_address: "I thum ********************"
    };

    useEffect(() => {
        const fetchLabelSettings = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const cusId = localStorage.getItem('cus_id');

                const response = await fetch(
                    'https://api.pickupxpress.com//V1/B2C/shipment/customize-label-view',
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'token': token || '',
                            'cus_id': cusId || ''
                        }
                    }
                );

                const result = await response.json();

                if (result.status === 'SUCCESS' && result.data) {
                    const data = result.data;

                    // Update visibility settings based on API response
                    setVisibilitySettings({
                        brand_logo: data.brand_logo === '1',
                        shipping_address: data.shipping_address === '1',
                        shipping_pincode: data.shipping_pincode === '1',
                        payment_mode: data.payment_mode === '1',
                        shipment_type: data.shipment_type === '1',
                        invoice_amount: data.invoice_amount === '1',
                        seller_name: data.seller_name === '1',
                        seller_address: data.seller_address === '1',
                        seller_pin: data.seller_pin === '1',
                        weight: data.weight === '1',
                        product_name: data.product_name === '1',
                        price: data.price === '1',
                        return_address: data.return_address === '1'
                    });

                    // Set existing logo if available
                    if (data.brand_logo_image) {
                        const logoUrl = data.brand_logo_image.startsWith('http')
                            ? data.brand_logo_image
                            : `https://api.pickupxpress.com/${data.brand_logo_image}`;
                        setExistingLogo(logoUrl);
                    } else {
                        setExistingLogo(null);
                    }
                } else {
                    setExistingLogo(null);
                }
            } catch (err) {
                console.error('Error fetching label settings:', err);
                toast.error('Error loading label settings');
                setExistingLogo(null);
            } finally {
                setLoading(false);
            }
        };

        fetchLabelSettings();
    }, []);

    const handleCheckboxChange = (field) => {
        setVisibilitySettings(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBrandLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setBrandLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeLogo = () => {
        setBrandLogoFile(null);
        setBrandLogoPreview(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const cusId = localStorage.getItem('cus_id');

            let dataToSend;
            let headers = {
                'token': token || '',
                'cus_id': cusId || ''
            };

            if (brandLogoFile) {
                const formData = new FormData();
                Object.keys(visibilitySettings).forEach(key => {
                    formData.append(key, visibilitySettings[key] ? '1' : '0');
                });
                formData.append('brand_logo_image', brandLogoFile);
                dataToSend = formData;
            } else {
                const jsonData = {};
                Object.keys(visibilitySettings).forEach(key => {
                    jsonData[key] = visibilitySettings[key] ? '1' : '0';
                });
                dataToSend = jsonData;
                headers['Content-Type'] = 'application/json';
            }

            const response = await axios.post(Base_Url + label, dataToSend, { headers });

            const result = response.data;
            if (result.status === "SUCCESS") {
                toast.success(result.message);
                setBrandLogoPreview(null);
                setBrandLogoFile(null);

                // Re-fetch to update existingLogo
                const updatedResponse = await fetch(
                    'https://api.pickupxpress.com//V1/B2C/shipment/customize-label-view',
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'token': token || '',
                            'cus_id': cusId || ''
                        }
                    }
                );

                const updatedResult = await updatedResponse.json();

                if (updatedResult.status === 'SUCCESS' && updatedResult.data) {
                    const data = updatedResult.data;
                    if (data.brand_logo_image) {
                        const logoUrl = data.brand_logo_image.startsWith('http')
                            ? data.brand_logo_image
                            : `https://api.pickupxpress.com/${data.brand_logo_image}`;
                        setExistingLogo(logoUrl);
                    }
                }
            } else {
                toast.error(result.message);
            }
        } catch (err) {
            toast.error('Error saving settings: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const toggleAll = (show) => {
        const newSettings = {};
        Object.keys(visibilitySettings).forEach(key => {
            newSettings[key] = show;
        });
        setVisibilitySettings(newSettings);
    };

    const formatWeight = () => {
        const weight = parseFloat(staticLabelData.weight);
        if (staticLabelData.partner?.toLowerCase() === 'bluedart') {
            return (weight / 1000).toFixed(2);
        }
        return weight.toFixed(2);
    };

    const getWeightUnit = () => {
        return staticLabelData.partner?.toLowerCase() === 'bluedart' ? 'KG' : 'Gram';
    };

    const Barcode = ({ value }) => (
        <svg width="280" height="80" viewBox="0 0 280 80" className="mx-auto">
            {Array.from({ length: 45 }).map((_, i) => {
                const width = Math.random() > 0.5 ? 2 : 4;
                const x = i * 6;
                return (
                    <rect
                        key={i}
                        x={x}
                        y="10"
                        width={width}
                        height="50"
                        fill="#000"
                    />
                );
            })}
            <text x="140" y="75" textAnchor="middle" fontSize="14" fontFamily="monospace" fontWeight="bold">
                {value}
            </text>
        </svg>
    );

    const isSellerSectionVisible = visibilitySettings.seller_name ||
        visibilitySettings.seller_address ||
        visibilitySettings.seller_pin;

    const isShippingAddressVisible = visibilitySettings.shipping_address ||
        visibilitySettings.shipping_pincode;

    const isPaymentSectionVisible = visibilitySettings.payment_mode ||
        visibilitySettings.shipment_type ||
        visibilitySettings.invoice_amount;

    const isProductSectionVisible = visibilitySettings.product_name ||
        visibilitySettings.price;

    return (
        <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
            <div className="z-10">
                <NavBar />
            </div>
            <div className="flex flex-1 overflow-hidden">
                {isToggleSidebar && (
                    <div
                        className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
                        style={
                            window.innerWidth < 768
                                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                                : {}
                        }>
                        <Sidebar />
                    </div>
                )}
                <div className={`min-h-screen dark:text-white w-full p-4 md:p-8 overflow-y-auto custom-scrollbar ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                    <div className={`max-w-full mx-auto ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                        <div className="mb-6 ml-64">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 dark:text-white">
                                Shipping Label Customizer
                            </h1>
                            <p className="text-gray-600 dark:text-white">
                                Toggle fields to customize your shipping label appearance
                            </p>
                        </div>
                        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 dark:text-white  ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                            <div className={`bg-white rounded-lg shadow-md p-6 dark:text-white dark:shadow-custom-dark ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white dark-shadow-custom-dark">
                                        Label Fields
                                    </h2>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => toggleAll(true)}
                                            className="text-sm px-3 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100 transition"
                                        >
                                            Show All
                                        </button>
                                        <button
                                            onClick={() => toggleAll(false)}
                                            className="text-sm px-3 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100 transition"
                                        >
                                            Hide All
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-3 mb-6 max-h-[600px] overflow-y-auto pr-2 dark:text-white dark-shadow-custom-dark">
                                    {Object.keys(fieldLabels).map((field) => (
                                        <div key={field}>
                                            <label className="flex items-center dark:text-white dark-shadow-custom-dark justify-between p-3 rounded-lg cursor-pointer transition border border-gray-200">
                                                <span className="text-gray-700 font-medium dark:text-white dark-shadow-custom-dark">
                                                    {fieldLabels[field]}
                                                </span>
                                                <div className="flex items-center gap-2 dark:text-white dark-shadow-custom-dark">
                                                    {visibilitySettings[field] ? (
                                                        <Eye className="w-4 h-4 text-green-600" />
                                                    ) : (
                                                        <EyeOff className="w-4 h-4 text-gray-400" />
                                                    )}
                                                    <input
                                                        type="checkbox"
                                                        checked={visibilitySettings[field]}
                                                        onChange={() => handleCheckboxChange(field)}
                                                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                                    />
                                                </div>
                                            </label>

                                            {field === 'brand_logo' && visibilitySettings[field] && (
                                                <div className= {`ml-3 mt-2 p-3 bg-gray-50 rounded-lg dark:text-white ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Upload Brand Logo
                                                    </label>

                                                    {brandLogoPreview ? (
                                                        <div className={`relative inline-block dark:text-white ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                                                            <img
                                                                src={brandLogoPreview}
                                                                alt="Brand Logo Preview"
                                                                className="h-20 w-auto border border-gray-300 rounded"
                                                            />
                                                            <button
                                                                onClick={removeLogo}
                                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <label className={`flex items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer dark:text-white hover:border-blue-500 transition ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}` }>
                                                            <div className="text-center">
                                                                <Upload className="w-6 h-6 mx-auto mb-1 text-gray-400" />
                                                                <span className="text-xs text-gray-500">Click to upload</span>
                                                            </div>
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={handleFileChange}
                                                                className="hidden"
                                                            />
                                                        </label>
                                                    )}
                                                    {brandLogoPreview && (
                                                        <p className="text-xs text-gray-500 mt-2">Logo preview ready for label</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="flex gap-3 mb-16">
                                    <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition font-medium"
                                    >
                                        <Save className="w-5 h-5" />
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                    <button
                                        disabled={loading}
                                        className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg disabled:bg-gray-100 transition"
                                    >
                                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                            </div>

                            <div className={`bg-white rounded-lg shadow-md p-6 ml-12 dark:text-white dark:shadow-custom-dark ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                                    Label Preview
                                </h2>

                                <div className={`bg-white border-4 border-black rounded-lg p-4 dark:text-white dark:shadow-custom-dark ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                                    <table className="w-full border-collapse">
                                        <tbody>
                                            <tr>
                                                <td colSpan="3" className={`border-2 border-black p-4 text-center bg-white dark:text-white dark:shadow-custom-dark ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                                                    <div className="flex justify-center items-center gap-8 mb-3">
                                                        {visibilitySettings.brand_logo && (
                                                            <>
                                                                <div className={`text-center dark:text-white dark:shadow-custom-dark ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                                                                    <p className="text-xs font-semibold dark:text-white dark:shadow-custom-dark text-gray-600 mb-2">Brand Logo</p>
                                                                    <img
                                                                        src={brandLogoPreview || existingLogo}
                                                                        alt="Brand Logo"
                                                                        className="h-16 mx-auto rounded p-1"
                                                                    />
                                                                </div>
                                                                <div className="h-24 w-px bg-gray-800 dark:text-white"></div>
                                                            </>
                                                        )}
                                                        <div className="text-center">
                                                            <p className="text-xs font-semibold text-gray-600 dark:text-white mb-2">Default Logo</p>
                                                            <img
                                                                src={placeholderLogo}
                                                                alt="Default Logo"
                                                                className="h-16 mx-auto rounded p-1"
                                                            />
                                                        </div>
                                                    </div>
                                                    <p className="font-bold text-sm">
                                                        Order: {staticLabelData.order_id}
                                                    </p>
                                                </td>
                                            </tr>

                                            {(isShippingAddressVisible || isPaymentSectionVisible) && (
                                                <tr>
                                                    {isShippingAddressVisible ? (
                                                        <td colSpan="2" className="border-2 border-black p-1 align-top text-xs">
                                                            {visibilitySettings.shipping_address && (
                                                                <>
                                                                    <p className="font-bold mb-1">Shipping Address</p>
                                                                    <p>{staticLabelData.first_name} {staticLabelData.last_name}</p>
                                                                    <p>{staticLabelData.drop_address}</p>
                                                                    <p>{staticLabelData.drop_city}, {staticLabelData.drop_state}</p>
                                                                </>
                                                            )}
                                                            {visibilitySettings.shipping_pincode && (
                                                                <p className="font-bold mt-1">
                                                                    PIN: {staticLabelData.drop_pin}/{staticLabelData.cluster_code}/{staticLabelData.destination_code}/{staticLabelData.destination_location}
                                                                </p>
                                                            )}
                                                        </td>
                                                    ) : (
                                                        <td colSpan="2" className="border-2 border-black p-1 align-top text-xs"></td>
                                                    )}
                                                    {isPaymentSectionVisible ? (
                                                        <td className="border-2 border-black p-3 align-top text-xs">
                                                            {visibilitySettings.payment_mode && (
                                                                <p className="font-bold">{staticLabelData.payment_mode.toUpperCase()}</p>
                                                            )}
                                                            {visibilitySettings.shipment_type && (
                                                                <p>{staticLabelData.shipping_mode}</p>
                                                            )}
                                                            {visibilitySettings.invoice_amount && (
                                                                <p>INR {staticLabelData.cod_amount}</p>
                                                            )}
                                                        </td>
                                                    ) : (
                                                        <td className="border-2 border-black p-3 align-top text-xs"></td>
                                                    )}
                                                </tr>
                                            )}

                                            {(isSellerSectionVisible || visibilitySettings.weight) && (
                                                <tr>
                                                    {isSellerSectionVisible ? (
                                                        <td colSpan="2" className="border-2 border-black p-3 align-top text-xs">
                                                            {visibilitySettings.seller_name && (
                                                                <p><span className="font-bold">Seller:</span> {staticLabelData.seller_name}</p>
                                                            )}
                                                            {visibilitySettings.seller_address && (
                                                                <p><span className="font-bold">Address:</span> {staticLabelData.seller_address}</p>
                                                            )}
                                                            {visibilitySettings.seller_pin && (
                                                                <p><span className="font-bold">Pin:</span> {staticLabelData.seller_pin}</p>
                                                            )}
                                                        </td>
                                                    ) : (
                                                        <td colSpan="2" className="border-2 border-black p-3 align-top text-xs"></td>
                                                    )}
                                                    {visibilitySettings.weight ? (
                                                        <td className="border-2 border-black p-3 align-top text-xs">
                                                            <p>
                                                                <span className="font-bold">Weight (in {getWeightUnit()}):</span><br />
                                                                {formatWeight()}
                                                            </p>
                                                        </td>
                                                    ) : (
                                                        <td className="border-2 border-black p-3 align-top text-xs"></td>
                                                    )}
                                                </tr>
                                            )}

                                            {isProductSectionVisible && (
                                                <>
                                                    <tr>
                                                        {visibilitySettings.product_name ? (
                                                            <th className="border-2 border-black p-2 text-xs font-bold">Product</th>
                                                        ) : (
                                                            <th className="border-2 border-black p-2 text-xs font-bold"></th>
                                                        )}
                                                        {visibilitySettings.price ? (
                                                            <>
                                                                <th className="border-2 border-black p-2 text-xs font-bold">Price</th>
                                                                <th className="border-2 border-black p-2 text-xs font-bold">Total</th>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <th className="border-2 border-black p-2 text-xs font-bold"></th>
                                                                <th className="border-2 border-black p-2 text-xs font-bold"></th>
                                                            </>
                                                        )}
                                                    </tr>
                                                    <tr>
                                                        {visibilitySettings.product_name ? (
                                                            <td className="border-2 border-black p-2 text-xs">
                                                                {staticLabelData.product_desc}
                                                            </td>
                                                        ) : (
                                                            <td className="border-2 border-black p-2 text-xs"></td>
                                                        )}
                                                        {visibilitySettings.price ? (
                                                            <>
                                                                <td className="border-2 border-black p-2 text-xs">
                                                                    INR {staticLabelData.total_amount}
                                                                </td>
                                                                <td className="border-2 border-black p-2 text-xs">
                                                                    INR {staticLabelData.total_amount}
                                                                </td>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <td className="border-2 border-black p-2 text-xs"></td>
                                                                <td className="border-2 border-black p-2 text-xs"></td>
                                                            </>
                                                        )}
                                                    </tr>
                                                </>
                                            )}

                                            <tr className='dark:text-white'>
                                                <td colSpan="3" className="border-2 border-black p-3 text-center dark:text-white">
                                                    <Barcode value={staticLabelData.waybill} className = "dark:text-white" />
                                                    <p className="font-bold text-xs">
                                                        {staticLabelData.partner === 'ecom' ? 'Ecom Express' :
                                                            staticLabelData.partner.charAt(0).toUpperCase() + staticLabelData.partner.slice(1)}
                                                    </p>
                                                </td>
                                            </tr>

                                            {visibilitySettings.return_address && (
                                                <tr>
                                                    <td colSpan="3" className="border-2 border-black p-3 text-xs">
                                                        <p className="font-bold mb-1">Return Address</p>
                                                        <p>{staticLabelData.return_address}</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShippingLabelCustomizer;